import { ColorEnum } from '@/constants/color'
import {
    productCellHeight,
    productRowHeight,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/constants'
import { useAnimatedBackgroundColorStyle } from '@/features/pos/components/ProductCatalog/parts/ProductList/parts/Product/hooks'
import Label from '@/features/pos/components/ProductCatalog/parts/ProductList/parts/Product/parts/Label'
import Name from '@/features/pos/components/ProductCatalog/parts/ProductList/parts/Product/parts/Name'
import Price from '@/features/pos/components/ProductCatalog/parts/ProductList/parts/Product/parts/Price'
import VariantQuantity from '@/features/pos/components/ProductCatalog/parts/ProductList/parts/Product/parts/VariantQuantity'
import { ItemMode } from '@/features/pos/components/ProductCatalog/parts/ProductList/types'
import Animated from '@/features/ui/animated'
import { useEnabledButtonState } from '@/features/ui/buttons/hooks/useEnabledButtonState'
import { FC, memo } from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface ContentProps {
    mode?: ItemMode
    name: string
    price: string
    soldOut?: boolean
    disabled?: boolean
    selected?: boolean
    variantQuantity?: number
    labelName?: string
    labelColor?: string
    style?: StyleProp<ViewStyle>
    onPress: () => void
}

const Content: FC<ContentProps> = ({
    mode = 'cell',
    name,
    price,
    soldOut,
    disabled = false,
    selected = false,
    variantQuantity = 1,
    labelName,
    labelColor,
    style,
    onPress,
}) => {
    const { state, eventHandlers } = useEnabledButtonState()

    const animatedStyle = useAnimatedBackgroundColorStyle(state)

    const hasVariants = variantQuantity > 1

    const { styles } = useStyles(stylesheet, { mode, soldOut, hasVariants })

    const getNameNumberOfLines = () => {
        if (mode === 'row') return 1

        return labelName ? 2 : 3
    }

    return (
        <Animated.Pressable
            {...eventHandlers}
            accessibilityRole="button"
            accessibilityState={{ disabled: disabled || soldOut, selected }}
            disabled={disabled || soldOut}
            onPress={onPress}
            style={[!selected && animatedStyle, styles.container, style]}
        >
            {labelName && <Label color={labelColor}>{labelName}</Label>}
            <View style={styles.innerContainer}>
                <Name numberOfLines={getNameNumberOfLines()} style={styles.name}>
                    {name}
                </Name>
                {hasVariants ? (
                    <VariantQuantity quantity={variantQuantity} />
                ) : (
                    <Price>{price}</Price>
                )}
            </View>
        </Animated.Pressable>
    )
}

const stylesheet = createStyleSheet({
    container: {
        alignItems: 'flex-start',
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        borderWidth: 1,
        variants: {
            mode: {
                cell: {
                    height: productCellHeight,
                },
                row: {
                    height: productRowHeight,
                },
            },
            soldOut: {
                true: {
                    backgroundColor: ColorEnum.GRAY_400,
                },
            },
        },
    },
    innerContainer: {
        flex: 1,
        alignItems: 'flex-start',
        maxWidth: '100%',
        padding: 12,
        paddingTop: 2,
        variants: {
            hasVariants: {
                false: {
                    gap: 12,
                },
                true: {
                    gap: 8,
                },
            },
        },
    },
    name: {
        marginTop: 'auto',
        variants: {
            soldOut: {
                true: {
                    color: ColorEnum.GRAY_600,
                },
            },
        },
    },
})

export default memo(Content)
